import {
  getFindingCvssDisplayValues,
  type FindingCvssFields,
} from './finding-cvss-score';

export const FINDING_CVSS_VERSIONS = ['4.0', '3.1'] as const;

export type FindingCvssVersionKey = (typeof FINDING_CVSS_VERSIONS)[number];

export const CVSS_SCORE_STATUSES = ['PENDING', 'SCORED', 'UNSCORED'] as const;

export type CvssScoreStatus = (typeof CVSS_SCORE_STATUSES)[number];

export const SEVERITY_SOURCES = ['AGENT', 'USER'] as const;

export type SeveritySource = (typeof SEVERITY_SOURCES)[number];

export const CVSS_SCORE_PENDING_LOCK_MS = 20 * 60 * 1000;

export const CVSS_SCORE_PENDING_LOCK_MESSAGE =
  'The CVSS score is still calculating. Try again when it finishes.';

export const CVSS_PUBLISH_REQUIRES_4_0_MESSAGE =
  'Score CVSS 4.0 and set the severity before you publish these findings.';

export const CVSS_SEVERITY_REASON_REQUIRED_MESSAGE =
  'Add an explanation for the severity label change.';

export type FindingCvssVersionSource<
  TRecord extends FindingCvssFields = FindingCvssFields,
> = {
  cvss?: TRecord | null;
  cvss40?: TRecord | null;
  cvss31?: TRecord | null;
  cvssVersion?: string | null;
};

export const resolveFindingCvssVersionRecord = <
  TRecord extends FindingCvssFields,
>({
  finding,
  version,
}: {
  finding: FindingCvssVersionSource<TRecord>;
  version: FindingCvssVersionKey;
}): TRecord | null => {
  const record = version === '4.0' ? finding.cvss40 : finding.cvss31;

  if (record) {
    return record;
  }

  if (finding.cvssVersion) {
    return null;
  }

  return finding.cvss?.version === version ? finding.cvss : null;
};

export const resolveFindingSelectedCvssVersion = (
  finding: FindingCvssVersionSource,
): FindingCvssVersionKey =>
  (finding.cvssVersion ?? finding.cvss?.version) === '3.1' ? '3.1' : '4.0';

export const formatFindingCvssPillScore = (
  cvss: FindingCvssFields | null | undefined,
): string | null => {
  const { score } = getFindingCvssDisplayValues(cvss);
  const value = score === null ? Number.NaN : Number(score);

  return Number.isFinite(value) ? value.toFixed(1) : null;
};

export type FindingCvssScoreState = {
  cvssScoreStatus?: CvssScoreStatus | null;
  cvssScoreRequestedAt?: Date | string | null;
  createdAt: Date | string;
};

export const isFindingCvssScorePendingLocked = ({
  finding,
  now,
}: {
  finding: FindingCvssScoreState;
  now: Date;
}): boolean => {
  if (finding.cvssScoreStatus !== 'PENDING') {
    return false;
  }

  const startedAtMs = new Date(
    finding.cvssScoreRequestedAt ?? finding.createdAt,
  ).getTime();

  return now.getTime() - startedAtMs < CVSS_SCORE_PENDING_LOCK_MS;
};

export const hasFindingSeverityLabel = (finding: {
  cvssScoreStatus?: CvssScoreStatus | null;
  severitySource?: SeveritySource | null;
}): boolean => !finding.cvssScoreStatus || !!finding.severitySource;
